import React, { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  TextField,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  DragIndicator as DragIndicatorIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const RecipientItem = ({ recipient, index, moveRecipient, onDelete, onUpdate }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'recipient',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'recipient',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveRecipient(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const handleChange = (field, value) => {
    onUpdate(index, { ...recipient, [field]: value });
  };

  return (
    <Box
      ref={(node) => drag(drop(node))}
      sx={{
        mb: 2,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ minWidth: '30px' }}>
          {index + 1}.
        </Typography>
        <TextField
          label="First Name"
          value={recipient.firstName}
          onChange={(e) => handleChange('firstName', e.target.value)}
          size="small"
          required
        />
        <TextField
          label="Last Name"
          value={recipient.lastName}
          onChange={(e) => handleChange('lastName', e.target.value)}
          size="small"
          required
        />
        <TextField
          label="Email"
          value={recipient.email}
          onChange={(e) => handleChange('email', e.target.value)}
          size="small"
          required
          sx={{ flexGrow: 1 }}
        />
        <IconButton 
          size="small" 
          onClick={() => onDelete(index)}
          sx={{ 
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.light',
            }
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const DocumentItem = ({ document, index, moveDocument, onDelete }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'document',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'document',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveDocument(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <Box
      ref={(node) => drag(drop(node))}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        borderRadius: 1,
        mb: 1
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          gap: 1
        }}
      >
        <DragIndicatorIcon 
          sx={{ 
            color: 'text.secondary',
            cursor: 'grab'
          }} 
        />
        <DescriptionIcon 
          sx={{ 
            color: '#1565c0',
            ml: 1
          }} 
        />
        <Typography 
          sx={{ 
            flexGrow: 1,
            ml: 1
          }}
        >
          {document.name}
        </Typography>
        <IconButton 
          size="small" 
          onClick={() => onDelete(index)}
          sx={{ 
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.light',
            }
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const SignatureConfirmPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const transactionData = location.state?.transactionData || {
    name: 'Insurance Document Signing' // 默认标题
  };

  const [documents, setDocuments] = useState([
    {
      id: 1,
      name: 'Electronic Signature Consent',
      type: 'Consent Form',
      status: 'Pending',
      required: true
    },
    {
      id: 2,
      name: 'Auto Insurance Application',
      type: 'Application Form',
      status: 'Pending',
      required: true
    },
    {
      id: 3,
      name: 'Tenant Insurance Application',
      type: 'Application Form',
      status: 'Pending',
      required: true
    },
    {
      id: 4,
      name: 'Pre-Authorized Credit (PAC) Form',
      type: 'Payment Form',
      status: 'Pending',
      required: true
    },
    {
      id: 5,
      name: 'AB Form',
      type: 'Insurance Form',
      status: 'Pending',
      required: true
    },
    {
      id: 6,
      name: 'Insurance Consent Form',
      type: 'Consent Form',
      status: 'Pending',
      required: true
    }
  ]);

  const [recipients, setRecipients] = useState([
    {
      id: 1,
      role: 'Agent',
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@brokerteam.ca'
    },
    {
      id: 2,
      role: 'Client',
      firstName: 'Michael',
      lastName: 'Brown',
      email: 'michael.brown@example.com'
    },
    {
      id: 3,
      role: 'Additional Insured',
      firstName: 'Sarah',
      lastName: 'Brown',
      email: 'sarah.brown@example.com'
    }
  ]);

  const moveRecipient = (fromIndex, toIndex) => {
    const newRecipients = [...recipients];
    const [movedItem] = newRecipients.splice(fromIndex, 1);
    newRecipients.splice(toIndex, 0, movedItem);
    setRecipients(newRecipients);
  };

  const handleDeleteRecipient = (index) => {
    const newRecipients = [...recipients];
    newRecipients.splice(index, 1);
    setRecipients(newRecipients);
  };

  const handleAddRecipient = () => {
    const newRecipient = {
      id: recipients.length + 1,
      role: 'Recipient',
      firstName: '',
      lastName: '',
      email: ''
    };
    setRecipients([...recipients, newRecipient]);
  };

  const handleAddMyself = () => {
    const myself = {
      id: recipients.length + 1,
      role: 'Agent',
      firstName: 'Current',
      lastName: 'User',
      email: 'current.user@brokerteam.ca'
    };
    setRecipients([myself, ...recipients]);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNext = () => {
    navigate('/signature-position', {
      state: { transactionData }
    });
  };

  const handleUpdateRecipient = (index, updatedRecipient) => {
    const newRecipients = [...recipients];
    newRecipients[index] = updatedRecipient;
    setRecipients(newRecipients);
  };

  const moveDocument = (fromIndex, toIndex) => {
    const newDocuments = [...documents];
    const [movedItem] = newDocuments.splice(fromIndex, 1);
    newDocuments.splice(toIndex, 0, movedItem);
    setDocuments(newDocuments);
  };

  const handleDeleteDocument = (index) => {
    const newDocuments = [...documents];
    newDocuments.splice(index, 1);
    setDocuments(newDocuments);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Container maxWidth="xl">
        {/* Hero Section */}
        <Box sx={{ 
          pt: 4,
          pb: 3,
          textAlign: 'center',
          position: 'relative',
          borderBottom: '1px solid #e0e0e0',
          mb: 4
        }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/signature')}
            startIcon={<ArrowBackIcon />}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            Back to Dashboard
          </Button>

          <Typography 
            variant="h4" 
            sx={{ 
              color: '#1976d2',
              fontWeight: 600,
              mb: 1
            }}
          >
            {transactionData.name}
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: '#666',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Add recipients and prepare documents for electronic signature
          </Typography>

          {/* Send Button */}
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Next
          </Button>
        </Box>

        {/* Documents Section */}
        <Paper sx={{ mb: 4, p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Documents
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1 
              }}
            >
              <DragIndicatorIcon />
              Drag to reorder documents
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            {documents.map((doc, index) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                index={index}
                moveDocument={moveDocument}
                onDelete={handleDeleteDocument}
              />
            ))}
          </Box>

          <Box 
            sx={{ 
              mt: 3, 
              p: 2, 
              border: '1px dashed grey', 
              borderRadius: 1, 
              textAlign: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.02)'
            }}
          >
            <CloudUploadIcon sx={{ color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Drag & drop files to upload
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            startIcon={<CloudUploadIcon />}
          >
            ADD DOCUMENT
          </Button>
        </Paper>

        {/* Recipients Section */}
        <Paper sx={{ mb: 4, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Recipients
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: 'text.primary',
                mb: 1
              }}
            >
              <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
              Drag this icon to change signing order
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
            >
              Required fields are marked with *
            </Typography>
          </Box>

          {recipients.map((recipient, index) => (
            <RecipientItem
              key={recipient.id}
              recipient={recipient}
              index={index}
              moveRecipient={moveRecipient}
              onDelete={handleDeleteRecipient}
              onUpdate={handleUpdateRecipient}
            />
          ))}

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleAddRecipient}
            >
              ADD RECIPIENT
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleAddMyself}
            >
              ADD MYSELF
            </Button>
          </Box>
        </Paper>
      </Container>
    </DndProvider>
  );
};

export default SignatureConfirmPage; 